import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Modal,
  Loader,
  Text,
  Stack,
  Group,
  keyframes,
  Box,
  ActionIcon,
} from '@mantine/core';
import { CustomButton } from '../common/Buttons/CustomButton';
import { ErrorMessage } from '../common/ErrorMessage/ErrorMessage';
import { useRealtimeTranscriptionContext } from 'src/contexts/RealtimeTranscriptionContext';
import Icon from '../common/Icons/Icons/Icon';

interface SpeechModalProps {
  opened: boolean;
  onClose: () => void;
  onComplete: (text: string) => void;
}

// Keyframes for animations
const wave = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  70% {
    transform: scale(1.2);
    opacity: 0;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

const recording = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// Heartbeat Waveform Visualizer Component with Bidirectional Bars
interface HeartbeatWaveformProps {
  audioLevel: number;
  isActive: boolean;
}

const HeartbeatWaveform = ({
  audioLevel,
  isActive,
}: HeartbeatWaveformProps) => {
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const animationRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);
  const BARS_COUNT = 50;
  const UPDATE_INTERVAL = 50; // Update every 50ms
  const VOLUME_THRESHOLD = 0.2; // Threshold below which bars become dots
  const MAX_BAR_EXTENSION = 20; // Maximum extension from baseline in pixels
  const MIN_BAR_EXTENSION = 1; // Minimum extension from baseline in pixels

  // Initialize waveform data
  useEffect(() => {
    setWaveformData(new Array(BARS_COUNT).fill(0));
  }, []);

  // Animate waveform from right to left with static bar heights
  useEffect(() => {
    if (!isActive) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Gradually fade out the waveform by reducing all values
      const fadeOut = () => {
        setWaveformData((prev) => {
          const newData = prev.map((val) => val * 0.95);
          // Continue fading if there are still visible bars
          if (newData.some((val) => val > 0.01)) {
            setTimeout(fadeOut, 50);
          }
          return newData;
        });
      };
      fadeOut();
      return;
    }

    const animate = (timestamp: number) => {
      if (timestamp - lastUpdateRef.current >= UPDATE_INTERVAL) {
        setWaveformData((prev) => {
          const newData = [...prev];
          // Shift all values to the left (each bar maintains its height)
          for (let i = 0; i < newData.length - 1; i++) {
            newData[i] = newData[i + 1];
          }
          // Add new value at the end (rightmost position) with current audio level
          newData[newData.length - 1] = audioLevel;
          return newData;
        });
        lastUpdateRef.current = timestamp;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [audioLevel, isActive]);

  const getBarExtension = (value: number) => {
    // Convert volume level to extension from baseline
    return Math.max(MIN_BAR_EXTENSION, value * MAX_BAR_EXTENSION);
  };

  const getBarColor = (value: number) => {
    if (value < VOLUME_THRESHOLD) {
      return '#94a3b8'; // Gray for low volume
    }
    return '#29748f';
  };

  const getBarOpacity = (value: number) => {
    // Bars with very low values are more transparent
    return Math.max(0.2, Math.min(0.9, value * 2 + 0.3));
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '280px',
        height: '40px',
        display: 'flex',
        alignItems: 'center', // Center align for baseline
        justifyContent: 'center',
        gap: '2px',
        zIndex: 10,
        pointerEvents: 'none',
        opacity: isActive ? 1 : 0.3,
        transition: 'opacity 0.3s ease',
      }}
    >
      {/* Central baseline */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '0',
          right: '0',
          height: '1px',
          backgroundColor: 'rgba(41, 116, 143, 0.3)',
          transform: 'translateY(-50%)',
          zIndex: 1,
        }}
      />

      {waveformData.map((value, index) => {
        const extension = getBarExtension(value);
        const isLowVolume = value < VOLUME_THRESHOLD;
        const opacity = getBarOpacity(value);
        const color = getBarColor(value);

        if (isLowVolume) {
          // Render as dot on the baseline
          return (
            <div
              key={`${index}-${Date.now()}`}
              style={{
                width: '3px',
                height: '3px',
                backgroundColor: color,
                borderRadius: '50%',
                opacity: opacity,
                flexShrink: 0,
                zIndex: 2,
                transition: 'opacity 0.1s ease-out',
              }}
            />
          );
        }

        // Render as bidirectional bar
        return (
          <div
            key={`${index}-${Date.now()}`}
            style={{
              width: '3px',
              height: `${extension * 2}px`, // Total height is extension above + below baseline
              backgroundColor: color,
              borderRadius: '2px',
              opacity: opacity,
              boxShadow: `0 0 4px ${color}40`,
              flexShrink: 0,
              zIndex: 2,
              transition: 'opacity 0.1s ease-out',
              // Create the bidirectional effect by centering the bar on the baseline
              position: 'relative',
            }}
          />
        );
      })}
    </div>
  );
};

// Enhanced Waveform component with circular visualization
interface WaveformProps {
  audioLevel: number;
  style?: React.CSSProperties;
}

const Waveform = ({ audioLevel, style }: WaveformProps) => {
  const points = 24; // Number of points in the circle
  const radius = 15; // Base radius of the circle
  const maxAmplitude = 15; // Maximum wave amplitude
  const rotationSpeed = 0.5; // Rotation speed in radians per second
  const rotationRef = useRef(0);
  const timeRef = useRef(0);
  const requestRef = useRef<number>();
  const [time, setTime] = useState(0);

  // Smooth the audio level for a more natural animation
  const smoothedLevel = useRef(0);
  smoothedLevel.current = smoothedLevel.current * 0.6 + audioLevel * 0.4;

  // Animate rotation
  useEffect(() => {
    const animate = (time: number) => {
      if (!timeRef.current) timeRef.current = time;
      const deltaTime = (time - timeRef.current) / 1000;
      timeRef.current = time;

      rotationRef.current =
        (rotationRef.current + rotationSpeed * deltaTime) % (Math.PI * 2);
      setTime((prev) => prev + deltaTime);

      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, []);

  // Generate points for the wave
  const getWavePoints = () => {
    const pointsArray = [];
    const angleStep = (Math.PI * 2) / points;
    const amplitude = maxAmplitude * smoothedLevel.current;

    for (let i = 0; i <= points; i++) {
      const angle = i * angleStep + rotationRef.current;
      const waveOffset = Math.sin(angle * 2 + time * 2) * amplitude;
      const pointRadius = radius + waveOffset;

      // Add some noise for organic feel
      const noise = Math.sin(angle * 5 + time * 3) * 2 * smoothedLevel.current;
      const x = Math.cos(angle) * (pointRadius + noise) + 50; // +50 to center in SVG viewBox
      const y = Math.sin(angle) * (pointRadius + noise) + 50;

      pointsArray.push(`${x},${y}`);
    }

    return pointsArray.join(' ');
  };

  // Generate particles
  const getParticles = () => {
    const particles = [];
    const particleCount = Math.floor(smoothedLevel.current * 10);

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance =
        radius + Math.random() * maxAmplitude * smoothedLevel.current * 1.5;
      const size = 1 + Math.random() * 3 * smoothedLevel.current;
      const opacity = 0.3 + Math.random() * 0.7 * smoothedLevel.current;

      // Add some movement
      const x = Math.cos(angle + time) * distance + 50;
      const y = Math.sin(angle + time * 1.2) * distance + 50;

      particles.push(
        <circle
          key={i}
          cx={x}
          cy={y}
          r={size}
          fill={`hsl(${200 + smoothedLevel.current * 60}, 80%, 60%)`}
          opacity={opacity}
        />
      );
    }

    return particles;
  };

  // Calculate gradient color based on audio level
  const getGradientColor = () => {
    const hue = 200 + smoothedLevel.current * 60; // Blue to cyan
    return `hsl(${hue}, 80%, 60%)`;
  };

  return (
    <Box
      style={{
        width: '100%',
        height: '120px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={style}
      >
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#29748f" />
            <stop offset="100%" stopColor={getGradientColor()} />
          </linearGradient>
          <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Background circle */}
        <circle
          cx="50%"
          cy="50%"
          r={radius * 0.9}
          fill="none"
          stroke="rgba(41, 116, 143, 0.2)"
          strokeWidth="1"
        />

        {/* Animated wave */}
        <polygon
          points={getWavePoints()}
          fill="url(#waveGradient)"
          opacity="0.8"
          filter="url(#glow)"
          style={{
            transition: 'all 0.1s ease-out',
            transformOrigin: 'center',
          }}
        />

        {/* Particles */}
        {getParticles()}

        {/* Center circle */}
        <circle
          cx="50%"
          cy="50%"
          r={radius * 0.2}
          fill={getGradientColor()}
          style={{
            transition: 'all 0.2s ease-out',
            transform: `scale(${1 + smoothedLevel.current * 0.2})`,
            filter: 'drop-shadow(0 0 4px rgba(41, 182, 246, 0.8))',
          }}
        />
      </svg>
    </Box>
  );
};

const SpeechModal = ({ opened, onClose, onComplete }: SpeechModalProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // New state for pause status
  const [isReviewing, setIsReviewing] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [isHolding, setIsHolding] = useState(false);
  const holdTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    isConnected,
    isRecording,
    audioLevel,
    transcript,
    completeTranscript,
    // accumulatedTranscript,
    error,
    startAgain,
    startRecording,
    stopRecording,
    resetTranscription,
    getAudioLevel,
    setAudioLevel,
  } = useRealtimeTranscriptionContext();

  // Handle hold to start recording
  const handleHoldStart = () => {
    setIsHolding(true);
    holdTimerRef.current = setTimeout(async () => {
      try {
        resetTranscription();
        await startRecording();
        analyzeAudio();
      } catch (err) {
        console.error('Failed to start recording:', err);
        setIsHolding(false);
      }
    }, 300); // 300ms delay before starting recording
  };

  // Handle release to stop recording

  const handleHoldEnd = async () => {
    setIsHolding(false);
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    if (isRecording) {
      setIsProcessing(true);
      await stopRecording();
      stopAudioAnalysis();

      // Wait longer for final transcription
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setIsProcessing(false);
    }
  };

  // Cancel hold if modal closes
  useEffect(() => {
    if (!opened && holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, [opened]);

  // Start recording when modal opens
  useEffect(() => {
    if (opened) {
      if (!isConnected) {
        startAgain();
      }
      resetTranscription();
      // startRecording().catch(console.error);
      setIsPaused(false); // Reset pause state when modal opens
    }
  }, [opened]);

  // Start audio analysis when recording starts
  useEffect(() => {
    if (isRecording) {
      analyzeAudio();
    } else {
      stopAudioAnalysis();
    }

    return () => {
      stopAudioAnalysis();
    };
  }, [isRecording]);

  useEffect(() => {
    // Wait for actual complete transcript only
    console.log('completeTranscript came', completeTranscript);
    if (completeTranscript.trim()) {
      console.log('completeTranscript came in', completeTranscript);
      setIsReviewing(true);
    }
  }, [completeTranscript]);

  const stopAudioAnalysis = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    setAudioLevel(0);
    analyserRef.current = null;
  }, [setAudioLevel]);

  const analyzeAudio = useCallback(async () => {
    try {
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Create audio context and analyzer
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      analyser.fftSize = 32;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Animation loop
      const updateMeter = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average volume
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;

        // Normalize to 0-1 range and apply some smoothing
        const normalized = Math.min(average / 255, 1);

        const newAudioLevel = (prev: number) => {
          const diff = Math.abs(prev - normalized);
          return diff > 0.02 ? normalized : prev;
        };

        // Only update if there's significant change to reduce re-renders
        setAudioLevel(newAudioLevel(audioLevel));

        animationFrameRef.current = requestAnimationFrame(updateMeter);
      };

      animationFrameRef.current = requestAnimationFrame(updateMeter);
    } catch (err) {
      console.error('Error accessing microphone:', err);
    }
  }, [setAudioLevel]);

  // const handleStop = async () => {
  //   setIsProcessing(true);
  //   await stopRecording();

  //   // Wait a short time to ensure any final transcription comes through
  //   await new Promise((resolve) => setTimeout(resolve, 1500));

  //   setIsProcessing(false);
  //   setIsReviewing(true);
  // };

  // const handlePauseResume = async () => {
  //   if (isPaused) {
  //     // Resume recording
  //     await startRecording();
  //   } else {
  //     // Pause recording
  //     await stopRecording();
  //   }
  //   setIsPaused(!isPaused);
  // };

  const handleRetry = async () => {
    setIsReviewing(false);
    resetTranscription();
    // await startRecording();
  };

  // const handleAddMore = async () => {
  //   setIsReviewing(false);
  //   resetTranscription();
  //   // await startRecording();
  // };

  const handleKeep = async () => {
    if (completeTranscript) {
      console.log('Keeping this transcript', completeTranscript.trim());
      onComplete(completeTranscript.trim());
    }
    await stopRecording();
    onClose();
  };

  console.log('TRANSCRIPT STATE', {
    transcript,
    completeTranscript,
    isReviewing,
  });

  return (
    <Modal
      opened={opened}
      onClose={async () => {
        await stopRecording();
        stopAudioAnalysis();
        onClose();
      }}
      centered
      radius="xl"
      size="md"
      withCloseButton={true}
    >
      {!isConnected ? (
        <Stack align="center" spacing="md">
          <Loader size="lg" color="dark" />
          <Text>Connecting to speech service...</Text>
        </Stack>
      ) : (
        <div style={{ position: 'relative' }}>
          <Stack align="center" spacing="xl" className="p-6 pt-0">
            {isRecording && (
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: '#ff3b30',
                  animation: `${pulse} 1.5s infinite`,
                  position: 'absolute',
                  top: '0px',
                  left: '24px',
                }}
              />
            )}

            <div
              style={{
                textAlign: 'center',
                marginTop: '0',
                transition: 'margin-top 0.3s ease',
              }}
            >
              <Text
                size="xl"
                fw={600}
                style={{
                  color: isRecording ? '#29748f' : '#212529',
                  marginBottom: '4px',
                  transition: 'all 0.3s ease',
                }}
              >
                {isReviewing
                  ? 'Review your message'
                  : isRecording
                  ? 'Speak now...'
                  : isHolding
                  ? 'Listening...'
                  : 'Hold to speak'}
              </Text>
              {!isReviewing && !isRecording && !isHolding && (
                <Text
                  size="sm"
                  c="dimmed"
                  style={{
                    marginTop: '4px',
                    animation: 'fadeIn 0.5s ease',
                  }}
                >
                  Press and hold the button to record
                </Text>
              )}
            </div>
            {/* Transcript display area */}
            <div
              style={{
                minHeight: '200px',
                maxHeight: '200px',
                overflowY: 'auto',
                width: '100%',
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6',
                border: '1px solid #e9ecef',
                boxShadow: 'inset 0 2px 4px 0 rgba(0,0,0,0.05)',
              }}
            >
              {isReviewing ? (
                <Text>
                  {completeTranscript ||
                    'Your transcribed text will appear here'}
                </Text>
              ) : (
                <Text size="sm" c="dimmed" ta="left">
                  {transcript ||
                    completeTranscript ||
                    'Start speaking to see transcription...'}
                </Text>
              )}
            </div>

            {/* Action buttons */}
            <Group
              position="center"
              spacing="md"
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '24px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              {isReviewing ? (
                <Group spacing="lg">
                  <ActionIcon
                    onClick={handleRetry}
                    size="xl"
                    radius="xl"
                    variant="filled"
                    color="blue"
                    title="Retry"
                    className="transition-all bg-red-500 hover:bg-red-600"
                  >
                    <Icon name="refresh" size="2rem" />
                  </ActionIcon>
                  <ActionIcon
                    onClick={handleKeep}
                    size="xl"
                    radius="xl"
                    variant="filled"
                    color="green"
                    title="Keep"
                    className="transition-all bg-[#29748f] hover:bg-[#29748f]/80"
                  >
                    <Icon name="check" size="2rem" />
                  </ActionIcon>
                </Group>
              ) : (
                <div
                  style={{
                    position: 'relative',
                    width: '72px',
                    height: '72px',
                    transition: 'all 0.3s ease',
                    transform: isHolding ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  {/* Waveform (only visible when recording/holding) */}
                  <div
                    style={{
                      position: 'absolute',
                      opacity: isRecording || isHolding ? 1 : 0,
                      transition: 'opacity 0.3s ease',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Waveform
                      audioLevel={getAudioLevel()}
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </div>

                  {/* Mic Button */}
                  <Box
                    onMouseDown={handleHoldStart}
                    onMouseUp={handleHoldEnd}
                    onMouseLeave={handleHoldEnd}
                    onTouchStart={handleHoldStart}
                    onTouchEnd={handleHoldEnd}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '24px',
                      background: isHolding
                        ? 'linear-gradient(145deg, #29748f, #1f5a75)'
                        : 'linear-gradient(145deg, #fff, #e9ecef)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: isHolding
                        ? '0 10px 25px -5px rgba(41, 116, 143, 0.5), 0 8px 10px -6px rgba(41, 116, 143, 0.3)'
                        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      position: 'relative',
                      zIndex: 1,
                      opacity: isRecording ? 0 : 1,
                      transform: isHolding ? 'scale(0.95)' : 'scale(1)',
                      border: isHolding
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        background: isHolding
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.02)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        transform: isHolding ? 'scale(0.9)' : 'scale(1)',
                      }}
                    >
                      <Icon
                        name="mic"
                        size="1.75rem"
                        color={isHolding ? 'white' : '#495057'}
                      />
                    </div>
                  </Box>
                  {isHolding && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '-30px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: '12px',
                        color: '#29748f',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        animation: 'fadeIn 0.3s ease',
                      }}
                    >
                      Release to stop
                    </div>
                  )}
                </div>
              )}
            </Group>
          </Stack>

          {isRecording && (
            <HeartbeatWaveform
              audioLevel={getAudioLevel()}
              isActive={isRecording}
            />
          )}
        </div>
      )}

      {error && (
        <ErrorMessage
          error={error.message}
          handleClose={() => {
            resetTranscription();
          }}
        />
      )}
    </Modal>
  );
};

export default SpeechModal;
